/* ── ONE REVIEW CARD, ONE FETCH, SHARED BY EVERY BLOCK ON A PAGE ────────────────────────────────
 *
 * The homepage renders reviews TWICE now: a short carousel high up and the full wall at the bottom.
 * Before this file the card renderer existed twice already — inline in sections/ts-home.liquid and
 * again in snippets/ts-reviews.liquid, the same forty lines with different element ids. A third copy
 * for the carousel would have put two renderers of the SAME reviews on ONE page, free to drift into
 * showing the same customer two different ways.
 *
 * ⚠ snippets/ts-reviews.liquid (the product, gift-card and digital pages) STILL HAS ITS OWN CARD
 * RENDERER, left alone deliberately: it carries the masonry and the write-a-review handler. Since
 * 2026-09-13 it shares this file's FETCH where the file is loaded: the product pages render the short
 * review row too, and two blocks fetching separately made two uncached requests for one answer. It
 * waits for DOMContentLoaded and falls back to its own fetch where this file is absent (the gift-card
 * and digital pages).
 *
 * ── THE FETCH IS SHARED, AND THAT IS THE OTHER HALF OF THE POINT ───────────────────────────────
 *
 * Two blocks asking the same endpoint is two requests for one answer, and two answers that can
 * differ: the proxy is edge-cached for 60s, so a carousel and a wall loading either side of an
 * expiry can legitimately disagree about what the newest review is. `load()` resolves once per page
 * and hands every caller the same array.
 */
window.TSReview = (function () {
  /* German, and any later language, arrives as window.TS_I18N (snippets/ts-i18n.liquid); English keeps
     the words written below. */
  var I18N = window.TS_I18N || {};
  function tr(key, en) { return I18N[key] || en; }
  var ENDPOINT = 'https://studio.togoandstark.com/api/reviews';
  var pending = null;

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  function stars(n) {
    n = Math.max(0, Math.min(5, Math.round(n || 0)));
    var s = '';
    for (var i = 1; i <= 5; i++) s += '<span class="ts-rev-star' + (i <= n ? ' on' : '') + '">★</span>';
    return s;
  }

  function fmtDate(d) {
    if (!d) return '';
    var dt = new Date(d);
    return isNaN(dt) ? '' : dt.toLocaleDateString(I18N.date_locale || 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  /* The card's markup is the wall's, byte for byte, so the carousel is visibly the same component.
     `opts.pic:false` drops the photograph for a caller whose row has to hold one height — the
     carousel — without changing anything else about the card. */
  function card(r, opts) {
    var wantPic = !opts || opts.pic !== false;
    var pic = wantPic && r.pictures && r.pictures[0]
      ? '<div class="ts-rev-pic"><img src="' + esc(r.pictures[0]) + '" alt="' + tr('rev_customer_photo', 'Customer photo') + '" loading="lazy" decoding="async"></div>'
      : '';
    var title = r.title ? '<div class="ts-rev-title">' + esc(r.title) + '</div>' : '';
    var init = (String(r.name || '?').trim().charAt(0) || '?').toUpperCase();
    return '<figure class="ts-rev-card">' + pic + '<div class="ts-rev-txt">' +
      '<div class="ts-rev-stars">' + stars(r.rating) + '</div>' +
      '<div class="ts-rev-who"><span class="ts-rev-av">' + esc(init) + '</span><span class="ts-rev-id"><b>' +
      esc(r.name || tr('rev_anonymous', 'Anonymous')) + '</b><time>' + esc(fmtDate(r.date)) + '</time></span></div>' +
      title + '<p class="ts-rev-body">' + esc(r.body) + '</p>' +
      '</div></figure>';
  }

  /**
   * A PHOTO-LED card: the customer's own picture, their words under it, their name.
   *
   * A different shape from card() rather than a flag on it, because it is a different argument. The
   * wall's card leads with stars and a name and treats a photo as a bonus; this one exists because
   * somebody photographed the thing on their own wall, and the words are the caption. Only a review
   * that HAS a picture can be one — callers filter first, and `hasPhoto` is here so they filter on
   * the same test this renders on.
   *
   * The body is clamped in CSS rather than truncated here: cutting a sentence in JavaScript means
   * guessing at a character count that is wrong at every other width, and a clamp reflows.
   */
  function photoCard(r) {
    return '<figure class="ts-revrow-card">' +
      '<div class="ts-revrow-pic"><img src="' + esc(r.pictures[0]) + '" alt="' + tr('rev_customer_photo_portrait', 'A customer photo of their portrait') + '" loading="lazy" decoding="async"></div>' +
      '<figcaption class="ts-revrow-cap">' +
        '<span class="ts-revrow-stars">' + stars(r.rating) + '</span>' +
        '<p class="ts-revrow-quote">' + esc(r.body) + '</p>' +
        '<cite class="ts-revrow-who">' + esc(r.name || tr('rev_anonymous', 'Anonymous')) + '</cite>' +
      '</figcaption></figure>';
  }

  function hasPhoto(r) { return !!(r && r.pictures && r.pictures[0]); }

  /**
   * The reviews, once per page. Resolves to an array, and to an EMPTY array on any failure —
   * never rejects, because every caller's correct response to "no reviews" and to "the proxy is
   * down" is the same: render nothing and get out of the way.
   *
   * ⚠ IT USED TO FAIL ON `shopify theme dev`, and that is worth knowing if it ever does again. The
   * proxy's allow-list was the live origin alone, so every local render of the wall was a CORS
   * rejection and an empty grid — nothing about these blocks could be looked at before customers
   * saw them. studio/app/api/reviews/route.ts now echoes localhost:9292 back as well.
   */
  function load() {
    if (pending) return pending;
    pending = fetch(ENDPOINT, { cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (d) { return (d && d.reviews) || []; })
      .catch(function () { return []; });
    return pending;
  }

  return { esc: esc, stars: stars, fmtDate: fmtDate, card: card, photoCard: photoCard, hasPhoto: hasPhoto, load: load, ENDPOINT: ENDPOINT };
})();
